import { CognitoUserPool, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { config } from './envConfig';

// Cognito Configuration - Garage User Pool
export const COGNITO_CONFIG = {
  UserPoolId: config.COGNITO_USER_POOL_ID,
  ClientId: config.COGNITO_CLIENT_ID,
  Region: config.COGNITO_REGION,
  Domain: config.COGNITO_DOMAIN,
  UserPoolName: 'omnicontext-test',
  UserPoolArn: 'arn:aws:cognito-idp:us-east-2:318596614352:userpool/us-east-2_avoOS0oEc',
};

// Create Cognito User Pool instance
export const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.UserPoolId,
  ClientId: COGNITO_CONFIG.ClientId,
});

// JWT Validation Constants
export const JWT_CONFIG = {
  issuer: `https://cognito-idp.${COGNITO_CONFIG.Region}.amazonaws.com/${COGNITO_CONFIG.UserPoolId}`,
  audience: COGNITO_CONFIG.ClientId,
  jwksUrl: `https://cognito-idp.${COGNITO_CONFIG.Region}.amazonaws.com/${COGNITO_CONFIG.UserPoolId}/.well-known/jwks.json`,
};

// Token expiration times (in milliseconds)
export const TOKEN_EXPIRY = {
  // NOTE: These are fallback values. The actual expiry is extracted from the JWT 'exp' claim.
  ACCESS_TOKEN: 60 * 60 * 1000, // 60 minutes
  ID_TOKEN: 60 * 60 * 1000,     // 60 minutes
  REFRESH_TOKEN: 30 * 24 * 60 * 60 * 1000, // 30 days
};

// Storage keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'cognito_access_token',
  ID_TOKEN: 'cognito_id_token',
  REFRESH_TOKEN: 'cognito_refresh_token',
  USER_ATTRIBUTES: 'cognito_user_attributes',
  SESSION_EXPIRY: 'cognito_session_expiry',
};

// Error messages mapping
export const COGNITO_ERRORS = {
  'UserNotFoundException': 'User not found',
  'NotAuthorizedException': 'Invalid credentials',
  'UserNotConfirmedException': 'User not confirmed. Please verify your email',
  'CodeMismatchException': 'Invalid verification code',
  'ExpiredCodeException': 'Verification code has expired',
  'LimitExceededException': 'Too many attempts. Please try again later',
  'InvalidPasswordException': 'Password does not meet security requirements',
  'UsernameExistsException': 'User already exists',
  'InvalidParameterException': 'Invalid parameters',
  'TooManyRequestsException': 'Too many requests. Please try again later',
  'default': 'An unexpected error occurred',
};

// Helper function to get user-friendly error message
export const getErrorMessage = (error) => {
  if (typeof error === 'string') {
    return COGNITO_ERRORS[error] || COGNITO_ERRORS.default;
  }

  // Check for specific error messages in originalError
  if (error?.originalError) {
    const originalMsg = error.originalError.message || error.originalError.toString() || '';
    const originalMsgLower = originalMsg.toLowerCase();

    // Check for specific Cognito error patterns
    if (originalMsgLower.includes('cannot reset password') &&
      (originalMsgLower.includes('no registered') || originalMsgLower.includes('no verified'))) {
      return 'Cannot reset password. This account does not have a verified email address. Please contact support or use a different account.';
    }

    if (originalMsgLower.includes('user does not exist') || originalMsgLower.includes('user not found')) {
      return 'User not found. Please check your username or email and try again.';
    }

    if (originalMsgLower.includes('not authorized') || originalMsgLower.includes('invalid credentials')) {
      return 'Invalid credentials. Please check your username or email and try again.';
    }
  }

  if (error?.code) {
    //for InvalidPasswordException, pass through Cognito's specific violation message
    if (error.code === 'InvalidPasswordException') {
      const originalMsg = error.originalError?.message || error.message;
      if (originalMsg) return originalMsg;
    }
    // For InvalidParameterException, check if we have a more specific message
    if (error.code === 'InvalidParameterException' && error.originalError) {
      const originalMsg = error.originalError.message || error.originalError.toString() || '';
      if (originalMsg.toLowerCase().includes('cannot reset password')) {
        return 'Cannot reset password. This account does not have a verified email address. Please contact support or use a different account.';
      }
    }
    return COGNITO_ERRORS[error.code] || error.message || COGNITO_ERRORS.default;
  }

  if (error?.message) {
    return error.message;
  }

  return COGNITO_ERRORS.default;
};

// Helper function to create user attributes
export const createUserAttributes = (attributes) => {
  return Object.entries(attributes).map(([key, value]) => {
    return new CognitoUserAttribute({
      Name: key,
      Value: value,
    });
  });
};
